import { Injectable } from '@angular/core';
import { routePathName } from '@app/app.models';
import { BroadcastKey } from '@app/services/broadcast/_models';
import { ISystemAlert, SystemAlertAction, SystemAlertType, SystemBlockingType } from '@app/services/system-alerts/_models';
import { I18nService } from '@localize/services';
import { BroadcastService } from '@services/broadcast/broadcast.service';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import each from 'lodash/each';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { v1 as uuid } from 'uuid';

import { ObjectString } from '../../../shared/utilities';
import { SystemAlertId } from './_constants';

@Injectable({
	providedIn: 'root'
})
export class SystemAlertsService extends OnDestroyMixin {
	allAlerts: any = {};
	timeouts: any = {};

	private i18nEmailNotValidated = this.i18nService.get('Email not validated', 'app.prompt.email-not-validated');
	private i18nValidateEmailMessage = this.i18nService.get('Validate email', 'app.message.check-email-for-validation-link');
	private i18nZone5AcceptancePending = this.i18nService.get('New legals', 'app.prompt.new-legal-document');
	private i18nAcceptLegalMessage = this.i18nService.get('There are legal documents to sign', 'app.message.new-legals-to-sign');

	private systemAlerts: ObjectString<ISystemAlert> = {
		[SystemAlertId.emailNotVerified]: {
			id: SystemAlertId.emailNotVerified,
			title: this.i18nEmailNotValidated,
			message: this.i18nValidateEmailMessage,
			type: SystemAlertType.critical,
			link: routePathName.account,
			blockingTypes: [
				SystemBlockingType.companyAll,
				SystemBlockingType.appkeyAll,
				SystemBlockingType.legalAll,
				SystemBlockingType.contactDelete
			]
		}
	};

	constructor(private broadcastService: BroadcastService, private i18nService: I18nService) {
		super();
	}

	public hasAlerts(): boolean {
		return Object.entries(this.allAlerts).length > 0;
	}

	/**
	 *
	 * @param type the blocking type you want to check
	 * return true if alert has the type in it's `blockingType` array, or any higher level
	 *
	 */
	public isAllowed(type: SystemBlockingType): boolean {
		return !this.isBlocked(type);
	}

	public isBlocked(type: SystemBlockingType): boolean {
		let blocked = false;
		each(this.allAlerts, (alert: ISystemAlert, id: string) => {
			const blockingTypes: string = get(alert, 'blockingTypes', []).join(',');
			if (blockingTypes.indexOf(type) >= 0) {
				blocked = true;
			}
		});
		return blocked;
	}

	public isActive(id: string): boolean {
		return !!this.allAlerts[id];
	}

	public anyActive(ids: string[]): boolean {
		let someActive = false;
		each(this.allAlerts, (alert: ISystemAlert, id: string) => {
			if (includes(ids, id)) {
				someActive = true;
			}
		});
		return someActive;
	}

	/**
	 *
	 * @param id the SystemAlertId constant, alerts that are pre-defined
	 */
	public systemAlert(id: string): string {
		const alert = this.systemAlerts[id];
		if (alert && !this.allAlerts[alert.id]) {
			return this.add(alert);
		}
		return;
	}

	/**
	 * add an alert, when passing in an alert that has an ID then it makes sure to reset/replace
	 */
	public add(alert: ISystemAlert): string {
		alert.id = alert.id || uuid();
		if (!this.allAlerts[alert.id]) {
			this.allAlerts[alert.id] = alert;
			this.broadcastService.send(BroadcastKey.system_alert_new, alert);
		}
		if (alert.timeout) {
			if (this.timeouts[alert.id]) {
				clearTimeout(this.timeouts[alert.id]);
				delete this.timeouts[alert.id];
			}
			this.timeouts[alert.id] = setTimeout(() => {
				this.cancel(alert.id, SystemAlertAction.timeout, true);
			}, alert.timeout);
		}
		return alert.id;
	}

	/**
	 * pass in an id to an alert to clean it after it was fixed
	 */
	public fixed(id: string) {
		this.cancel(id, SystemAlertAction.completed, true);
	}

	/**
	 *
	 * @param id => ID to an alert (ignored if no match)
	 * @param action SystemAlertAction = completed | timeout | cancelled
	 * @param triggerCallback [optional] defaults to false
	 */
	public cancel(id: string, action: SystemAlertAction, triggerCallback: boolean = false) {
		const alert = this.allAlerts[id];
		if (alert) {
			if (this.timeouts[id]) {
				clearTimeout(this.timeouts[id]);
				delete this.timeouts[id];
			}
			if (triggerCallback) {
				if (alert.callback) {
					alert.callback(action);
				}
			}
			this.broadcastService.send(BroadcastKey.system_alert_cancelled, alert);
			delete this.allAlerts[alert.id];
		}
	}

	public removeAll() {
		each(this.allAlerts, (value: ISystemAlert, id: string) => {
			this.broadcastService.send(BroadcastKey.system_alert_cancelled, value);
			this.remove(value);
		});
	}

	private remove(alert: ISystemAlert) {
		if (this.allAlerts[alert.id]) {
			if (this.timeouts[alert.id]) {
				clearTimeout(this.timeouts[alert.id]);
				delete this.timeouts[alert.id];
			}
			delete this.allAlerts[alert.id];
		}
	}
}
